import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { getPlayerLessons } from "../utils/firestore.utils"
import { schoolContext } from "../providers/school.provider"

// Le lezioni di un allievo si leggono a blocchi: i partecipanti non sono
// indicizzati, quindi si scorre lo storico della scuola dalla più recente
const usePlayerLessons = (player, { pageSize = 10 } = {}) => {
	const { selectedSchool } = useContext(schoolContext)

	const [lessons, setLessons] = useState([])
	const [isLoading, setLoadingState] = useState(false)
	const [isLoadingMore, setLoadingMoreState] = useState(false)
	const [hasMore, setHasMore] = useState(false)
	const [hasError, setErrorState] = useState(false)

	const cursorRef = useRef(null)
	// ogni ricarica invalida le risposte delle richieste precedenti
	const requestIDRef = useRef(0)

	const playerID = player?.playerID

	const loadPage = useCallback(
		async ({ isFirstPage }) => {
			if (!playerID || !selectedSchool) return

			const requestID = requestIDRef.current
			isFirstPage ? setLoadingState(true) : setLoadingMoreState(true)
			setErrorState(false)

			try {
				const result = await getPlayerLessons(selectedSchool, player, {
					pageSize,
					cursor: isFirstPage ? null : cursorRef.current,
				})

				if (requestID !== requestIDRef.current) return

				cursorRef.current = result.cursor
				setHasMore(result.hasMore)
				setLessons(prev => (isFirstPage ? result.lessons : [...prev, ...result.lessons]))
			} catch (error) {
				console.error("Errore caricando le lezioni dell'allievo:", error)
				if (requestID === requestIDRef.current) setErrorState(true)
			} finally {
				if (requestID === requestIDRef.current) {
					setLoadingState(false)
					setLoadingMoreState(false)
				}
			}
		},
		[playerID, selectedSchool, pageSize, player],
	)

	const reload = useCallback(() => {
		requestIDRef.current += 1
		cursorRef.current = null
		setLessons([])
		setHasMore(false)
		loadPage({ isFirstPage: true })
	}, [loadPage])

	const loadMore = useCallback(() => {
		if (isLoadingMore || isLoading || !hasMore) return
		loadPage({ isFirstPage: false })
	}, [isLoading, isLoadingMore, hasMore, loadPage])

	useEffect(() => {
		if (!playerID || !selectedSchool) return
		reload()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [playerID, selectedSchool])

	return { lessons, isLoading, isLoadingMore, hasMore, hasError, loadMore, reload }
}

export default usePlayerLessons
